/**
 * Browser API base — reads hostMatrix + REACT_APP_* only (no inline host strings).
 *
 * Avoid importing environments.js here (webpack circular init with appConfig).
 *
 * ***** Code Start BH28082026_1115 - Sticky absolute API fallback on IIS/SPA misroutes
 * IIS SPA hosts often answer /api/* with 406 (Accept: application/json) or HTML.
 * Detect that once, pin the matrix LLM origin for the tab, and retry.
 */
import hostMatrix from '../config/candor_host_matrix.json';

const STICKY_SESSION_KEY = 'candor_api_base_override';
const STORAGE_BACKEND_KEY = 'candor_backend_target';

/** Standard recognized backend targets for dynamic runtime switching. */
export const BACKEND_TARGETS = {
  dev: 'https://candordev.candorsys.com',
  qa: 'https://candorqa.candorsys.com',
  qa_legacy: 'https://ultradocuments.com',
  preprod: 'https://candorlin.candorsys.com',
  prod: 'https://candoride.candorsys.com',
  local: 'http://127.0.0.1:8001',
};

/** Prefer JSON; include wildcard Accept so IIS content-negotiation does not hard-406. */
export const API_JSON_ACCEPT = 'application/json, text/plain;q=0.9, */*;q=0.8';

function trimSlash(url) {
  return String(url || '')
    .trim()
    .replace(/\/$/, '');
}

function appEnv() {
  const raw = String(process.env.REACT_APP_ENVIRONMENT || '')
    .trim()
    .toLowerCase();
  if (raw === 'qa' || raw === 'staging' || raw === 'stage') return 'qa';
  if (raw === 'production' || raw === 'prod') return 'production';
  if (raw === 'dev_server' || raw === 'devserver') return 'dev_server';
  if (raw === 'development' || raw === 'dev' || raw === 'local') return 'development';
  if (process.env.NODE_ENV === 'production') return 'production';
  return 'development';
}

function matrixLlm(envName) {
  const row = (hostMatrix.environments || {})[envName] || {};
  return trimSlash(row.llm_api_url || '');
}

function fallbackBase() {
  const env = appEnv();
  if (env === 'qa') return matrixLlm('qa') || BACKEND_TARGETS.qa;
  if (env === 'production') return matrixLlm('production') || BACKEND_TARGETS.prod;
  if (env === 'dev_server') return matrixLlm('dev_server') || BACKEND_TARGETS.dev;
  // development: empty so setupProxy can forward /api
  return '';
}

/** Always-absolute matrix LLM origin for the active env (incl. local DEV). */
export function absoluteMatrixApiBaseUrl() {
  const env = appEnv();
  if (env === 'qa') return matrixLlm('qa') || BACKEND_TARGETS.qa;
  if (env === 'production') return matrixLlm('production') || BACKEND_TARGETS.prod;
  if (env === 'dev_server') return matrixLlm('dev_server') || BACKEND_TARGETS.dev;
  return matrixLlm('development') || BACKEND_TARGETS.local;
}

let _memorySticky = '';

/** Check URL query string or HashRouter for ?backend=dev|qa / ?api=dev|qa / ?env=dev|qa. */
export function detectUrlBackendOverride() {
  if (typeof window === 'undefined' || !window.location) return '';
  try {
    const search = window.location.search || '';
    const hash = window.location.hash || '';
    let params = new URLSearchParams(search);
    if (!params.get('backend') && !params.get('api') && !params.get('env') && hash.includes('?')) {
      params = new URLSearchParams(hash.substring(hash.indexOf('?')));
    }
    const val = (params.get('backend') || params.get('api') || params.get('env') || '').trim().toLowerCase();
    if (!val) return '';
    if (val === 'qa' || val === 'candorqa') return BACKEND_TARGETS.qa;
    if (val === 'qa_alt' || val === 'qa_legacy' || val === 'ultradocuments') return BACKEND_TARGETS.qa_legacy;
    if (val === 'dev' || val === 'dev_server' || val === 'candordev') return BACKEND_TARGETS.dev;
    if (val === 'preprod' || val === 'candorlin') return BACKEND_TARGETS.preprod;
    if (val === 'prod' || val === 'production' || val === 'candoride') return BACKEND_TARGETS.prod;
    if (val === 'local' || val === 'localhost') return BACKEND_TARGETS.local;
    if (val.startsWith('http://') || val.startsWith('https://')) {
      return trimSlash(val);
    }
  } catch (_) {}
  return '';
}

function readStoredBackend() {
  if (_memorySticky) return _memorySticky;
  const urlOverride = detectUrlBackendOverride();
  if (urlOverride) {
    pinApiBaseUrl(urlOverride);
    return urlOverride;
  }
  try {
    if (typeof sessionStorage !== 'undefined') {
      const sess = trimSlash(sessionStorage.getItem(STICKY_SESSION_KEY) || '');
      if (sess) {
        _memorySticky = sess;
        return sess;
      }
    }
    if (typeof localStorage !== 'undefined') {
      const stored = trimSlash(
        localStorage.getItem(STORAGE_BACKEND_KEY) ||
        localStorage.getItem(STICKY_SESSION_KEY) ||
        ''
      );
      if (stored) {
        _memorySticky = stored;
        return stored;
      }
    }
  } catch (_) {}
  return '';
}

/** Pin API origin for this tab and persist across reloads. */
export function pinApiBaseUrl(base) {
  const b = trimSlash(base);
  if (!b) return;
  _memorySticky = b;
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(STICKY_SESSION_KEY, b);
    }
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_BACKEND_KEY, b);
      localStorage.setItem(STICKY_SESSION_KEY, b);
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('candor-backend-changed', { detail: { base: b } }));
    }
  } catch (_) {
    /* private mode */
  }
}

/** Clear any explicit backend pin/override and revert to build default. */
export function clearApiBasePin() {
  _memorySticky = '';
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(STICKY_SESSION_KEY);
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(STORAGE_BACKEND_KEY);
      localStorage.removeItem(STICKY_SESSION_KEY);
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('candor-backend-changed', { detail: { base: '' } }));
    }
  } catch (_) {}
}

/**
 * Returns human-readable ID for the active backend ('dev' | 'qa' | 'prod' | etc.).
 */
export function getActiveBackendId() {
  const current = resolveApiBaseUrl();
  for (const [id, url] of Object.entries(BACKEND_TARGETS)) {
    if (current === url) return id;
  }
  if (!current) return 'local';
  return 'custom';
}

/**
 * Switch backend dynamically (accepts 'dev', 'qa', 'prod', or a full URL).
 */
export function setBackendTarget(target) {
  const norm = String(target || '').trim().toLowerCase();
  const url = BACKEND_TARGETS[norm] || (target && target.startsWith('http') ? trimSlash(target) : '');
  if (url) {
    pinApiBaseUrl(url);
    return url;
  }
  return '';
}

/**
 * Resolve the failover target if the current backend is down or misrouting.
 */
export function getFailoverTarget(currentBase) {
  const curr = trimSlash(currentBase || resolveApiBaseUrl());
  if (curr === BACKEND_TARGETS.dev) {
    return BACKEND_TARGETS.qa;
  }
  if (curr === BACKEND_TARGETS.qa) {
    return BACKEND_TARGETS.qa_legacy;
  }
  if (curr === BACKEND_TARGETS.qa_legacy) {
    return BACKEND_TARGETS.dev;
  }
  return '';
}

/**
 * Automatically determine the most suitable backend based on use case:
 * 1. Path/feature-specific requirements (e.g. Yahoo SSO requires candorqa; GitLab requires candordev).
 * 2. Active hostname/domain (visiting via candorqa/ultradocuments -> QA; candordev -> DEV; candorsys -> PROD).
 * 3. URL query/hash parameters (?backend=qa / ?backend=dev).
 * 4. Stored user/session preference.
 */
export function autoDetectUseCaseBackend(path = '') {
  // 1. Path & provider specific use-case detection
  const p = String(path || '').toLowerCase();
  if (p.includes('provider=yahoo') || p.includes('provider_id=yahoo') || p.includes('/sso/callback/yahoo')) {
    // Yahoo SSO console is registered with callback on candorqa.candorsys.com
    return BACKEND_TARGETS.qa;
  }
  if (p.includes('provider=gitlab') || p.includes('provider_id=gitlab')) {
    // GitLab is configured specifically for Dev Server
    return BACKEND_TARGETS.dev;
  }

  // 2. Query / hash parameter intent (e.g. ?backend=qa, ?backend=dev)
  const urlParamOverride = detectUrlBackendOverride();
  if (urlParamOverride) {
    return urlParamOverride;
  }

  // 3. Stored preference if explicitly set
  const stored = readStoredBackend();
  if (stored) {
    return stored;
  }

  // 4. Hostname / Domain use case auto-detection
  if (typeof window !== 'undefined' && window.location) {
    const host = String(window.location.hostname || '').toLowerCase();
    if (host.includes('candorqa') || host.includes('ultradocuments') || host.startsWith('qa.')) {
      return BACKEND_TARGETS.qa;
    }
    if (host.includes('candordev') || host.startsWith('dev.')) {
      return BACKEND_TARGETS.dev;
    }
    if (host.includes('candorlin')) {
      return BACKEND_TARGETS.preprod;
    }
    if (host === 'candorsys.com' || host === 'www.candorsys.com') {
      return BACKEND_TARGETS.prod;
    }
  }

  // 5. Default build environment fallback
  const fromEnv = process.env.REACT_APP_API_BASE_URL;
  if (fromEnv !== undefined && String(fromEnv).trim() !== '') {
    return trimSlash(fromEnv);
  }
  return fallbackBase();
}

/** @returns {string} Absolute API origin, or '' for same-origin proxy */
export function resolveApiBaseUrl(path = '') {
  return autoDetectUseCaseBackend(path);
}

/**
 * Join a path with the resolved API base.
 * @param {string} path e.g. '/api/v1/auth/login'
 * @returns {string}
 */
export function apiUrl(path) {
  const raw = path == null ? '' : String(path);
  const p = raw.startsWith('/') ? raw : raw ? `/${raw}` : '';
  const base = resolveApiBaseUrl(p);
  if (!base) {
    return p || '/';
  }
  return `${base.replace(/\/$/, '')}${p}`;
}

function normalizeApiPath(path) {
  const raw = path == null ? '' : String(path);
  return raw.startsWith('/') ? raw : raw ? `/${raw}` : '/';
}

/**
 * True when the response is from an SPA/static host instead of the LLM API.
 * @param {Response} res
 * @param {string} [bodyText]
 */
export function isApiMisrouteResponse(res, bodyText) {
  if (!res) return false;
  if (res.status === 406 || res.status === 405) return true;
  const ct = String(res.headers.get('content-type') || '').toLowerCase();
  if (ct.includes('text/html')) return true;
  if (typeof bodyText === 'string' && /^\s*<(!doctype\s+html|html\b)/i.test(bodyText)) {
    return true;
  }
  return false;
}

function sameOriginUrl(a, b) {
  try {
    const left = new URL(a, typeof window !== 'undefined' ? window.location.href : 'http://localhost');
    const right = new URL(b, typeof window !== 'undefined' ? window.location.href : 'http://localhost');
    return left.origin === right.origin && left.pathname === right.pathname;
  } catch (_) {
    return a === b;
  }
}

/**
 * fetch() with automatic failover between Dev and QA backends when the
 * primary URL encounters a connection error, 502/503/504, or an IIS/SPA misroute (406 / HTML).
 * @param {string} path API path starting with /api/...
 * @param {RequestInit} [init]
 * @returns {Promise<Response>}
 */
export async function apiFetch(path, init = {}) {
  const p = normalizeApiPath(path);
  const headers = new Headers(init.headers || {});
  if (!headers.has('Accept')) {
    headers.set('Accept', API_JSON_ACCEPT);
  }
  const nextInit = { ...init, headers };

  const primaryUrl = apiUrl(p);
  let res;
  let networkFailed = false;

  try {
    res = await fetch(primaryUrl, nextInit);
  } catch (err) {
    networkFailed = true;
  }

  let isBad = networkFailed;
  if (res) {
    if (res.status >= 502 && res.status <= 504) {
      isBad = true;
    } else {
      let peek = '';
      try {
        peek = await res.clone().text();
      } catch (_) {}
      if (isApiMisrouteResponse(res, peek)) {
        isBad = true;
      }
    }
  }

  if (!isBad && res) {
    return res;
  }

  // Attempt failover to alternate backend
  const currentBase = resolveApiBaseUrl();
  const failover = getFailoverTarget(currentBase) || absoluteMatrixApiBaseUrl();
  if (!failover || failover === currentBase) {
    if (res) return res;
    throw new Error(`Failed to fetch ${primaryUrl}`);
  }

  const failoverUrl = `${failover}${p}`;
  if (sameOriginUrl(primaryUrl, failoverUrl)) {
    if (res) return res;
    throw new Error(`Failed to fetch ${primaryUrl}`);
  }

  try {
    const failoverRes = await fetch(failoverUrl, nextInit);
    if (failoverRes && (failoverRes.ok || failoverRes.status < 500)) {
      pinApiBaseUrl(failover);
      return failoverRes;
    }
  } catch (_) {}

  if (res) return res;
  throw new Error(`Failed to fetch from both ${primaryUrl} and ${failoverUrl}`);
}

// Global browser hooks for QA/Dev testing in console
if (typeof window !== 'undefined') {
  window.setCandorBackend = setBackendTarget;
  window.getCandorBackend = getActiveBackendId;
  window.getCandorBackendUrl = resolveApiBaseUrl;
  window.clearCandorBackendPin = clearApiBasePin;
  window.CANDOR_BACKENDS = BACKEND_TARGETS;
}

const apiBase = {
  BACKEND_TARGETS,
  resolveApiBaseUrl,
  apiUrl,
  apiFetch,
  pinApiBaseUrl,
  clearApiBasePin,
  getActiveBackendId,
  setBackendTarget,
  getFailoverTarget,
  detectUrlBackendOverride,
  absoluteMatrixApiBaseUrl,
  isApiMisrouteResponse,
  API_JSON_ACCEPT,
};
export default apiBase;
